import type { CplatApiResponse } from '@/types/api';
import type { OrderItem, OrderList } from '@/types/order-list';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useOrderDetails } from '../_components/order-details/OrderDetailsProvider';
import { useOrderStickyLayout } from '../_contexts/OrderStickyLayoutContext';

type InfiniteOrderListQueryFn = (params: {
  skip: number;
  take: number;
}) => Promise<CplatApiResponse<OrderList[]>>;

type UseInfiniteOrderListParams = {
  queryKey: readonly unknown[];
  queryFn: InfiniteOrderListQueryFn;
  pageSize: number;
  enabled?: boolean;
};

const ORDER_LIST_ROOT_MARGIN = '320px 0px';

const getScrollableParent = (node: HTMLElement) => {
  let current: HTMLElement | null = node.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const isScrollable =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight;

    if (isScrollable) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
};

const getFirstOrderList = (page: CplatApiResponse<OrderList[]> | undefined) => page?.data[0];

const getNextSkip = (pages: CplatApiResponse<OrderList[]>[], pageSize: number) =>
  pages.length * pageSize;

export const useInfiniteOrderList = ({
  queryKey,
  queryFn,
  pageSize,
  enabled = true,
}: UseInfiniteOrderListParams) => {
  const listEndSentinelRef = useRef<HTMLDivElement | null>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) => {
        const skip = typeof pageParam === 'number' ? pageParam : 0;
        return queryFn({ skip, take: pageSize });
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const pagination = getFirstOrderList(lastPage)?.pagination;

        if (!pagination || !pagination.hasNext) {
          return undefined;
        }

        return getNextSkip(allPages, pageSize);
      },
      enabled,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
    });

  const { restoreScrollOnLoad } = useOrderStickyLayout();

  // 탭 전환/필터(마켓·날짜·검색·필터버튼) 변경으로 queryKey 가 바뀌면 로딩(전체 스피너)으로
  // 콘텐츠가 붕괴되어 스크롤이 상단으로 강제된다. isLoading=false(로드 완료) 시점에
  // min(변경 직전 위치, 제목 높이 T)로 복원해 compact 는 보존하면서 리스트는 1행부터 보인다.
  const renderedKeyRef = useRef<string | null>(null);
  const queryKeyString = JSON.stringify(queryKey);

  useEffect(() => {
    if (isLoading) return;
    if (renderedKeyRef.current === queryKeyString) return;
    renderedKeyRef.current = queryKeyString;
    restoreScrollOnLoad();
  }, [isLoading, queryKeyString, restoreScrollOnLoad]);

  // 주문 수집(주문 업데이트)은 queryKey 가 그대로라 위 효과가 발동하지 않는다.
  // 수집 완료(true→false, 리패치까지 끝난 시점)를 감지해 동일하게 복원한다.
  const { isCollectingOrders } = useOrderDetails();
  const wasCollectingRef = useRef(isCollectingOrders);

  useEffect(() => {
    const wasCollecting = wasCollectingRef.current;
    wasCollectingRef.current = isCollectingOrders;
    if (wasCollecting && !isCollectingOrders) {
      restoreScrollOnLoad();
    }
  }, [isCollectingOrders, restoreScrollOnLoad]);

  const orderList = useMemo<OrderItem[]>(() => {
    const pages = data?.pages ?? [];
    return pages.flatMap((page) => getFirstOrderList(page)?.orders ?? []);
  }, [data?.pages]);

  const pages = data?.pages ?? [];
  const firstPage = getFirstOrderList(pages[0]);
  const lastPage = getFirstOrderList(pages[pages.length - 1]);
  const pagination = lastPage?.pagination ?? firstPage?.pagination;

  useEffect(() => {
    const target = listEndSentinelRef.current;

    if (!target || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const scrollRoot = getScrollableParent(target);
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry?.isIntersecting) {
          return;
        }

        void fetchNextPage();
      },
      {
        root: scrollRoot,
        rootMargin: ORDER_LIST_ROOT_MARGIN,
        threshold: 0,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, orderList.length]);

  return {
    orderList,
    counts: firstPage?.counts,
    pagination,
    availableActionCount: firstPage?.availableActionCount ?? 0,
    listEndSentinelRef,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  };
};
